import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Permissions } from '@app/core/guard/Permissions';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { NdfOperationFilteredSummary } from '@interfaces/a-organizar/ndfOperationsFilteredSummary';
import { PTaxQuotation } from '@interfaces/a-organizar/pTaxQuotation';
import { Option } from '@interfaces/common/option';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';

export interface CardOperationSummaryAction {
  action:
    | 'edit'
    | 'duplicate'
    | 'remove'
    | 'toHire'
    | 'toSale'
    | 'toQuote'
    | 'toSimulated'
    | 'revert-liquidation-to-open'
    | 'revert-hired-to-quoted'
    | 'mtm'
    | 'create-committee';
  subAction?: 'full' | 'partial' | 'partial-before';
  item: NdfOperationFilteredSummary | null;
}

@Component({
  selector: 'app-ndf-operation-card-summary',
  templateUrl: './ndf-operation-card-summary.component.html',
  styleUrls: ['./ndf-operation-card-summary.component.scss'],
  standalone: false,
})
export class NdfOperationCardSummaryComponent extends VaultBaseComponent implements OnInit {
  @Input() ptaxQuotesDates: PTaxQuotation[] | null = null;
  @Input() operation: NdfOperationFilteredSummary | null = null;
  @Input() currencyOptions: Option[] = [];
  @Input() isMobile: boolean = false;
  @Input() hasActions: boolean = true;
  @Input() isQuotable: boolean = false;
  @Input() isSimulatable: boolean = false;
  @Input() isHirable: boolean = true;
  @Input() isLiquidatable: boolean = false;
  @Input() isSalable: boolean = false;
  @Input() isLiquidated: boolean = false;
  @Input() isClosable: boolean = false;
  @Output()
  sendOperationCardSummaryAction: EventEmitter<CardOperationSummaryAction> =
    new EventEmitter<CardOperationSummaryAction>();

  isCollapsed: boolean | null = false;

  actionIcons: MenuItem[] = [
    {
      label: this.t(this.tk.General.Delete),
      icon: 'pi pi-trash',
      action: 'remove',
    },
  ];

  liquidationOptions: Option[] = [];
  operationPermissions = Permissions.DerivativeCalculator;
  selectedLiquidationOption: Option | null = null;

  constructor(translateService: TranslateService) {
    super(translateService);
  }

  ngOnInit(): void {
    this.fillLiquidationOptions();

    this.actionIcons.unshift({
      label: 'Criar comitê',
      icon: 'pi pi-users',
      action: 'create-committee',
    });

    if (this.operation?.operationStatus === 'Hired') {
      this.actionIcons.unshift({
        label: 'MTM',
        icon: 'pi pi-chart-line',
        action: 'mtm',
      });
    }
  }

  fillLiquidationOptions(): void {
    this.liquidationOptions = [
      {
        label: this.t(this.tk.Management.NdfBeforeMaturity),
        value: 'partial-before',
      },
      {
        label: this.t(this.tk.Management.NdfPartial),
        value: 'partial',
      },
    ];

    if (
      this.verifyDateTimeSale(
        this.operation?.operationDate || '',
        this.operation?.dueAt || '',
        this.operation?.internationalCurrencyId
      )
    ) {
      this.liquidationOptions.unshift({
        label: this.t(this.tk.Management.NdfFullOnMaturity),
        value: 'full',
      });
    }
  }

  verifyDateTimeSale(
    operationDate: string,
    dueAt: string,
    internationalCurrencyId: string | undefined
  ): boolean {
    if (!operationDate || !dueAt) {
      return false;
    }

    const nCurrentDate = new Date();
    const nOperationDate = new Date(operationDate);
    const nDueAt = new Date(dueAt);

    // Original condition
    if (nDueAt >= nOperationDate && nDueAt <= nCurrentDate) {
      return true;
    }

    if (this.ptaxQuotesDates) {
      // New condition: (dueAt - 1 day) == today's date (without considering time)
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Zero out time components

      const dueAtMinusOneDay = new Date(nDueAt);
      dueAtMinusOneDay.setDate(dueAtMinusOneDay.getDate() - 1);
      dueAtMinusOneDay.setHours(0, 0, 0, 0); // Zero out time components

      const isDueAtMinusOneDayToday = dueAtMinusOneDay.getTime() === today.getTime();

      // Check if ptaxQuotesDates contains an item with the given internationalCurrencyId
      const hasMatchingCurrencyId =
        this.ptaxQuotesDates &&
        this.ptaxQuotesDates.some(item => item.internationalCurrencyId === internationalCurrencyId);

      return isDueAtMinusOneDayToday && hasMatchingCurrencyId ? true : false;
    }

    return false;
  }

  handleAction(menuItem: MenuItem) {
    const { action } = menuItem;
    this.handleCardSummaryAction(action, this.operation);
  }

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  handleOptionChange(selectedOptions: Option | null): void {
    this.handleCardSummaryAction(
      'toSale',
      this.operation,
      selectedOptions?.value as 'full' | 'partial' | 'partial-before'
    );
    this.selectedLiquidationOption = null;
  }

  handleCardSummaryAction(
    action:
      | 'edit'
      | 'duplicate'
      | 'remove'
      | 'toHire'
      | 'toSale'
      | 'toQuote'
      | 'toSimulated'
      | 'revert-liquidation-to-open'
      | 'revert-hired-to-quoted'
      | 'mtm'
      | 'create-committee',
    item: NdfOperationFilteredSummary | null,
    subAction?: 'full' | 'partial' | 'partial-before'
  ) {
    this.sendOperationCardSummaryAction.emit({ action, item, subAction });
  }

  get selectedLiquidation(): string {
    return this.selectedLiquidationOption as unknown as string;
  }

  get currencyLabel(): string {
    return (
      this.currencyOptions.find(
        currency => currency.value === this.operation?.internationalCurrencyId
      )?.label || ''
    );
  }

  get operationStatusLabel(): string {
    switch (this.operation?.operationStatus) {
      case 'Simulated':
        return this.t(this.tk.Management.NdfSimulated);
      case 'Quoted':
        return this.t(this.tk.Management.NdfQuoted);
      case 'Hired':
        return this.t(this.tk.Management.NdfHired);
      default:
        return '';
    }
  }

  get financialStatusLabel(): string {
    switch (this.operation?.financialStatus) {
      case 'Open':
        return this.t(this.tk.Management.NdfOpen);
      case 'Settled':
        return this.t(this.tk.Management.NdfSettled);
      case 'Reversed':
        return this.t(this.tk.Management.NdfSettledReversed);
      default:
        return '';
    }
  }

  get compositionLabel(): string {
    switch (this.operation?.composition) {
      case 'Full':
        return this.t(this.tk.Management.NdfFull);
      case 'Partial':
        return this.t(this.tk.Management.NdfPartial);
      default:
        return '';
    }
  }

  get operationStrategyTypeLabel(): string {
    switch (this.operation?.operationStrategyType) {
      case 'NDFVanilla':
        return 'NDF Vanilla';
      case 'NDFCAP':
        return 'NDF CAP';
      case 'Callspread':
        return 'Call Spread';
      case 'CallVanilla':
        return 'Call Vanilla';
      case 'SWAP':
        return 'SWAP';
      default:
        return '';
    }
  }

  get isCallStrategy(): boolean {
    return ['CALLSPREAD', 'CALLVANILLA'].includes(
      this.operation?.operationStrategyType?.toUpperCase() || ''
    );
  }

  get isSwapStrategy(): boolean {
    return this.operation?.operationStrategyType === 'SWAP';
  }

  getInterestFlowLabel(value: string | undefined): string {
    if (!value) return '-';
    switch (value) {
      case 'Monthly':
        return 'Mensal';
      case 'Quarterly':
        return 'Trimestral';
      case 'Biannually':
        return 'Semestral';
      case 'Annually':
        return 'Anual';
      default:
        return value;
    }
  }

  get translationKeys() {
    return {
      NdfPremiumPercentage: 'Prêmio %',
      NdfPremiumAnnualized: 'Prêmio % a.a.',
      NdfMaxProtectionPercentage: 'Proteção cambial máxima %',
      NdfTotalAnnualCost: 'Custo anual total',
    };
  }

  getAmortizationFlowLabel(value: string | undefined): string {
    if (!value) return '-';
    switch (value) {
      case 'Monthly':
        return this.t(this.tk.Management.Monthly);
      case 'Quarterly':
        return this.t(this.tk.Management.Quarterly);
      case 'Biannually':
        return 'Semestral';
      case 'Annually':
        return 'Anual';
      case 'Bullet':
        return 'Bullet';
      default:
        return value;
    }
  }
  // amortizationFlowOptions: Option[] = [
  //   { label: 'Mensal', value: 'this.t(this.tk.Management.Monthly)' },
  //   { label: 'this.t(this.tk.Management.Quarterly)', value: 'Quarterly' },
  //   { label: 'Semestral', value: 'Biannually' },
  //   { label: 'Anual', value: 'Annually' },
  //   { label: 'Bullet', value: 'Bullet' },

  getIndexerLabel(indexerValue: string | undefined): string {
    if (!indexerValue) return '-';
    switch (indexerValue) {
      case 'USDBRL':
        return 'USD';
      case 'EURBRL':
        return 'EUR';
      case 'CDI':
        return 'CDI';
      default:
        return indexerValue;
    }
  }

  getSequentialId(): string {
    if (this.operation?.demandSummary?.comexOperation?.sequentialId) {
      return this.operation?.demandSummary?.comexOperation.sequentialId;
    }
    if (this.operation?.demandSummary?.loanOperation?.sequentialId) {
      return this.operation?.demandSummary?.loanOperation.sequentialId;
    }
    return this.operation?.demandSummary?.sequentialId || '';
  }

  getUrlOperation(): string {
    if (this.operation?.demandSummary?.comexOperation?.sequentialId) {
      return '/dashboard/comex-operation/v1';
    }
    if (this.operation?.demandSummary?.loanOperation?.sequentialId) {
      return `/dashboard/costs/debt/management/launch-installments/${this.operation?.demandSummary?.loanOperation?.id}`;
    }
    return '/dashboard/ndf-calculator/list';
  }
}
